import * as React from "react";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import {
  DatePicker,
  PickersCalendarHeader,
  PickersDay,
} from "@mui/x-date-pickers";
import moment from "moment";
import Tooltip from "@mui/material/Tooltip";
import DateRangeOutlinedIcon from "@mui/icons-material/DateRangeOutlined";
import { Box, Typography } from "@mui/material";
import { time } from "@/modules/crm/global/constants";
import GetIcon from "@/components/font-awesome-icon";
import { faSpinner } from "@fortawesome/free-solid-svg-icons/faSpinner";
import CircularLoader from "@/components/circular-loader";

// Custom DatePicker Icon Component
const CustomDatePickerIcon = ({ title, ...props }) => (
  <Tooltip title={title} placement="top-end">
    <DateRangeOutlinedIcon
      sx={{
        color: "var(--palette-text)",
        width: "19px",
        marginRight: "6px",
        pointerEvents: "auto",
        ...props?.sx,
      }}
      {...props}
    />
  </Tooltip>
);

// Custom PickersDay Component
const ServerDay = ({ day, outsideCurrentMonth, loadingDays, ...other }) => {
  const isLoading =
    !outsideCurrentMonth &&
    loadingDays.includes(day.format(time.format.dateOnly));
  return (
    <Box
      sx={{
        position: "relative",
        pointerEvents: isLoading ? "none" : "initial",
      }}
    >
      {isLoading ? (
        <Box
          sx={{
            position: "absolute",
            width: "100%",
            height: "100%",
            left: "3px",
            top: "1px",
            textAlign: "center",
            paddingTop: "3px",
            zIndex: 2,
            background: "rgba(255,255,255,0.8)",
          }}
        >
          <GetIcon icon={faSpinner} />
        </Box>
      ) : undefined}
      <PickersDay
        {...other}
        outsideCurrentMonth={outsideCurrentMonth}
        day={day}
      />
    </Box>
  );
};

export default function UIDatePicker({
  name,
  value,
  title,
  placeholder,
  onChange,
  onBlur = () => {},
  error,
  helperText,
  disabled = false,
  size = "small",
  sx = {},
  format,
  tooltip = "",
  minDate,
  maxDate,
  isLocal = true,
  wrapperClasss = "",
  shouldDisableDate = () => false,
  loadingDays = [],
  asyncDays = false,
  onMonthChange = () => {},
  loadingText = "Loading..",
  required = false,
}) {
  const handleOnChange = (date) => {
    if (format && date) {
      const formattedDate = date?.format(format);
      onChange({ value: formattedDate });
      return;
    }
    onChange(date);
  };

  const localDate = value
    ? isLocal
      ? moment.utc(value).local()
      : moment.utc(value)
    : null;

  const [isOpen, setIsOpen] = React.useState(false);
  const [focused, setFocused] = React.useState(false);
  const isLabelShrunk = !!localDate || isOpen || focused;

  const dateTimeformat = format || "DD-MMM-YYYY";

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <div className={wrapperClasss}>
        <DatePicker
          onOpen={() => setIsOpen(true)}
          onClose={() => setIsOpen(false)}
          shouldDisableDate={shouldDisableDate}
          slots={{
            openPickerIcon: (props) =>
              !!loadingDays?.length ? (
                <CircularLoader
                  iconSize="16px"
                  sx={{ height: "auto", width: "auto", marginRight: "6px" }}
                />
              ) : (
              <CustomDatePickerIcon title={tooltip} {...props} />
            ),
            calendarHeader: (props) => {
              return (
                <>
                  {" "}
                  {!!loadingDays?.length && (
                    <Box
                      sx={{
                        position: "absolute",
                        background:
                          "color-mix(in srgb, var(--palette-background) 80%, transparent)",
                        width: "100%",
                        height: "100%",
                        borderRadius: "5px",
                        top: 0,
                        left: 0,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        zIndex: 1,
                        flexDirection: "column",
                        gap: "1rem",
                      }}
                    >
                      <CircularLoader
                        iconSize="18px"
                        sx={{ height: "auto", width: "auto" }}
                      />
                      <Typography>{loadingText}</Typography>
                    </Box>
                  )}
                  <PickersCalendarHeader {...props} />
                </>
              );
            },
            day: (props) =>
              asyncDays ? (
                <ServerDay {...props} loadingDays={loadingDays} />
              ) : (
                <PickersDay
                  {...props}
                  sx={{
                    color: "var(--palette-text)",
                    "&:hover": {
                      backgroundColor: "var(--palette-secondary_button_hover)",
                    },
                    "&.Mui-selected:hover": {
                      backgroundColor: "var(--palette-hover_color)",
                    },
                    "&:focus.Mui-selected": {
                      backgroundColor: "var(--palette-hover_color)",
                    },
                    "&.Mui-disabled": {
                      color: "var(--palette-disabled_button_text) !important",
                    },
                  }}
                />
              ),
          }}
          format={dateTimeformat}
          slotProps={{
             textField: {
              required: required,
              size: size,
              placeholder: placeholder || dateTimeformat || "",
              helperText: error ? helperText || error : "",
              error: error,
              required: required,
              onFocus: () => setFocused(true),
              onBlur: (e) => {
                setFocused(false);
                onBlur(e);
              },
              InputLabelProps: {
                style: {
                  color: error
                    ? "#d32f2f"
                    : disabled
                      ? "var(--palette-disabled_button_text)"
                      : "var(--palette-grey_text)",
                  maxWidth: "calc(100% - 48px)",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                },
              },
              sx: { minHeight: 56, ...sx },
              InputProps: {
                style: {
                  color: "var(--palette-text)",
                  background: disabled
                    ? "var(--palette-disabled_button)"
                    : "var(--palette-grid_table)",
                  paddingRight: isLabelShrunk ? "0" : "40px",
                },
              },
            },
            openPickerButton: {
              sx: {
                position: "absolute",
                left: 0,
                top: 0,
                width: "100%",
                height: "100%",
                justifyContent: "flex-end",
                borderRadius: 0,
              },
            },
            calendarHeader: {
              sx: {
                color: "var(--palette-text) !important",
                "& .MuiPickersCalendarHeader-switchViewButton": {
                  color: "var(--palette-text) !important",
                },
              },
            },
            leftArrowIcon: {
              sx: {
                color: "var(--palette-grey_text) !important",
              },
            },
            rightArrowIcon: {
              sx: {
                color: "var(--palette-grey_text) !important",
              },
            },
            popper: {
              sx: {
                "& .MuiDayCalendar-weekDayLabel": {
                  color: "var(--palette-grey_text) !important",
                },
                "& .MuiPickersYear-root .MuiPickersYear-yearButton.Mui-selected":
                  {
                    backgroundColor: "var(--palette-primary_button) !important",
                  },
                "& .MuiPickersYear-root .MuiPickersYear-yearButton:hover": {
                  backgroundColor:
                    "var(--palette-secondary_button_hover) !important",
                },
              },
            },
          }}
          name={name}
          label={title || ""}
          value={localDate}
          onChange={handleOnChange}
          onBlur={onBlur}
          disabled={disabled}
          sx={{ ...sx }}
          minDate={minDate ? moment.utc(minDate).local() : undefined}
          maxDate={maxDate ? moment.utc(maxDate).local() : undefined}
          onMonthChange={onMonthChange}
        />
      </div>
    </LocalizationProvider>
  );
}
